import { BrowserModule } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, inject, APP_INITIALIZER, ErrorHandler, NgModule, NO_ERRORS_SCHEMA, provideAppInitializer } from '@angular/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppComponent } from './app.component';
import { LoginComponent } from './login/login.component';
import { HomeComponent } from './home/home.component';
import { AppRoutingModule } from './app-routing.module';
import { SecondMenuComponent } from './second-menu/second-menu.component';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { HttpErrorInterceptor } from './interceptor-httperror';
import { SharedModule } from './shared/shared.module';
import { UtilitiesComponent } from './utilities/utilities.component';
import { SupportComponent } from './support/support.component';
import {TooltipModule} from 'primeng/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import {CardModule} from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { providePrimeNG } from 'primeng/config';
import  Lara  from '@primeng/themes/lara';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import {provideNativeDateAdapter} from '@angular/material/core';
import { OktaAuthModule, OKTA_CONFIG } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { environment } from 'src/environments/environment';
import { ErrorHandlerComponent } from './error-handler/error-handler.component';
import { LoginPreloaderComponent } from './login-preloader/login-preloader.component';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SessionTimedComponent } from './session-timed/session-timed.component';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { OktaAuthInterceptor } from './services/okta-auth.interceptor';
import { RuntimeConfigService } from './services/runtime-config.service';
import { LoggerService } from './services/logger/logger.service';
import { AppInsightsLoggerService } from './services/logger/app-insights-logger.service';
import { FallbackLoggerService } from './services/logger/fallback-logger.service';
import { LOGGER_TOKEN } from './services/logger/ilogger';
import { GlobalErrorHandlerService } from './error-handler/global-error-handler.service';
import { SignedOutComponent } from './signed-out/signed-out.component';

const initializeRuntimeConfig = (runtimeConfigService: RuntimeConfigService) => () => runtimeConfigService.load();
import { FilterRegistryService } from './services/filter-registry.service';

// Okta configuration
const oktaAuth = new OktaAuth({
  issuer: environment.okta.issuer,
  clientId: environment.okta.clientId,
  redirectUri: environment.okta.redirectUri,
  scopes: environment.okta.scopes,
  pkce: true,
  storageManager: {
    token: {
      storageType: 'sessionStorage',
      storageTypes: []
    }
  },
  services: {
    syncStorage: false
  }
});

const getLoginSearchParam = (searchParams: URLSearchParams, ...keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = searchParams.get(key);
    if (value) {
      return value;
    }
  }

  return undefined;
};

export const onAuthRequired = async (_oktaAuth: OktaAuth) => {
  const searchParams = new URLSearchParams(window.location.search);
  const internal = getLoginSearchParam(searchParams, 'internal') || environment.okta.idp;
  const forceOktaLogin = window.sessionStorage.getItem('force-okta-login') === 'true';
  const signInOptions: any = forceOktaLogin ? { prompt: 'login' } : {};

  if (forceOktaLogin) {
    window.sessionStorage.removeItem('force-okta-login');
  }

  if (internal !== 'true' && environment.envType !== 'internal') {
    await _oktaAuth.signInWithRedirect({ ...signInOptions, idp:environment.okta.externalIdpId});
    return;
  }

  await _oktaAuth.signInWithRedirect(signInOptions);
};

@NgModule({ 
  declarations: [
    AppComponent,
    LoginComponent,
    HomeComponent,
    SecondMenuComponent,
    UtilitiesComponent,
    SupportComponent,
    ErrorHandlerComponent,
    LoginPreloaderComponent,
    SessionTimedComponent,
    SignedOutComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  bootstrap: [AppComponent], 
  imports: [
    NgbModule,
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    DialogModule,
    SharedModule,
    OktaAuthModule,
    TooltipModule,
    NgSelectModule,
    ToastModule,
    ScrollPanelModule,
    CardModule,
    ButtonModule,
    ConfirmDialogModule,
  ], 
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeRuntimeConfig,
      deps: [RuntimeConfigService],
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService
    },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    provideNativeDateAdapter(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OktaAuthInterceptor,
      multi: true
    },
    {
      provide: OKTA_CONFIG,
      useValue: { oktaAuth, onAuthRequired }
    },
    provideAppInitializer(() => {
      inject(FilterRegistryService).register();
    }),
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: "[data-theme='dark-mode']"
        }
      }
    }),
    // Okta services are provided by OktaAuthModule
    DialogService,
    MessageService,
    {
      provide: LOGGER_TOKEN,
      useExisting: LoggerService
    },
    LoggerService,
    AppInsightsLoggerService,
    FallbackLoggerService,
    provideHttpClient(withInterceptorsFromDi())
  ] 
})
export class AppModule { }
